import React, { useEffect, ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DetailSection } from 'app/views/components/detailSections/detail-section'
import { DealParams } from 'app/models/deal-params'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import { loadOneDeal, setOneDeal } from 'app/state/ducks/deals/thunks'
import { getDeal, getDeals } from 'app/state/ducks/deals/selectors'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { getContacts } from 'app/state/ducks/contacts/selectors'
import { getDealAmendments } from 'app/state/ducks/deal-amendments/selectors'
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors'
import { loadCalendars } from 'app/state/ducks/holiday-calendars/thunks'
import { DataBoxLayout } from 'app/views/components/detailSections/data-box-layout'
import { DetailSectionHeader } from 'app/views/components/detailSections/detail-section-header'
import { buildDealDetailSections } from 'app/views/components/functions/buildDetailsObjects'
import DealRelatedFacilitiesDetails from './deal-related-facilities-details'
import { getFacilities } from 'app/state/ducks/facilities/selectors'


export default function DealDetailsInfo() {
    const { dealId, status } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const entityIdToName = new Map<string, string>()
    const deal: DealParams | undefined = useSelector(getDeal)
    const entities = useSelector(getEntities)
    const contacts = useSelector(getContacts)
    const facilities = useSelector(getFacilities)
    const dealAmendments: DealParams[] = useSelector(getDealAmendments)
    const allDeals: DealParams[] = useSelector(getDeals)
    const holidayCalendars = useSelector(getCalendars)

    const relatedFacilities = facilities.filter(facility => facility.dealId.id === dealId)

    const availableCurrencies = Array.from(
        new Set(relatedFacilities.flatMap(facility => facility.currencies))
    );


    useEffect(() => {
        if (!dealId) {
            return
        }
        dispatch(loadAllFacilities())
        dispatch(loadEntities())
        dispatch(loadContacts())
        dispatch(loadCalendars())

        if (allDeals) {
            const selectedDeal = allDeals.find(deal => deal.id === dealId)
            if (selectedDeal) {
                dispatch(setOneDeal(selectedDeal))
            } else {
                dispatch(loadOneDeal(dealId, status ?? 'Draft'))
            }
        }
        if (dealAmendments) {
            const amended_deal = dealAmendments.find(
                amendment => amendment.id === dealId
            )
            if (amended_deal) {
                amended_deal.status = amended_deal.status + ' Amendment'
                dispatch(setOneDeal(amended_deal))
            }
        }
    }, [dealId])

    useEffect(() => {
        if (Array.isArray(entities)) {
            for (const entity of entities) {
                if (entity.id) {
                    entityIdToName.set(cleanUUID(entity.id), entity.entityName)
                }
            }
        }
    }, [entities])

    const {
        dealDetailsObjectArray,
        dealIdentifiersObjectArray,
        dealFinancialObjectArray,
        dealContactsObjectArray
    } = deal ? buildDealDetailSections(deal, entities, contacts, holidayCalendars, availableCurrencies, navigate) : {
        dealDetailsObjectArray: [],
        dealIdentifiersObjectArray: [],
        dealFinancialObjectArray: [],
        dealContactsObjectArray: []
    };

    const renderDetailSection = (headerText: string, detailObjArr: any[], columnCount: number) => (
        <>
            <DetailSectionHeader headerText={headerText} />
            <DetailSection detailObjArr={detailObjArr} columnCount={columnCount} />
        </>
    );

    const renderTableSection = (headerText: string, tableSection: ReactNode) => (
        <>
            <DetailSectionHeader headerText={headerText} />
            {tableSection}
        </>
    );


    return (
        <DataBoxLayout
            sections={[
                [renderDetailSection('Details', dealDetailsObjectArray, 3), renderDetailSection('Identifiers', dealIdentifiersObjectArray, 3)],
                [renderDetailSection('Financial', dealFinancialObjectArray, 2), renderDetailSection('Contacts', dealContactsObjectArray, 2)]
            ]}
            tableSections={[renderTableSection('Related Facilities', <DealRelatedFacilitiesDetails />)]}
        />
    )
}